import { create } from "zustand";

const DEFAULT_MESSAGE = "Waiting for the next suggestion...";

export interface ScriptItem {
  id: string;
  text: string;
  isComplete: boolean;
  isWarmUp: boolean;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface RecapItem {
  title: string;
  description: string;
  status?: "confirmed" | "unconfirmed" | "not_discussed";
}

export interface TranscriptEvent {
  id: string;
  timestamp: number;
  speaker: "sdr" | "customer" | "system";
  text: string;
  isQuestion?: boolean;
  metadata?: Record<string, unknown>;
}

interface ScriptState {
  currentItem: ScriptItem | null;
  warmUpItem: string | null;
  isWaitingForNewWarmUp: boolean;
  history: ScriptItem[];
  transcriptEvents: TranscriptEvent[];
  recapItems: RecapItem[];
  callComplete: boolean;
  callOutcome?: string;
  hasActiveCall: boolean;

  // Actions
  insertScriptItem: (
    text: string,
    tags?: string[],
    metadata?: Record<string, unknown>,
  ) => void;
  warmUpNextScriptItem: (text: string) => void;
  completeScriptItem: () => void;
  addTranscriptEvent: (event: TranscriptEvent) => void;
  setRecapItems: (items: RecapItem[]) => void;
  completeCall: (outcome: string) => void;
  checkForScriptItems: () => boolean;
  setHasActiveCall: (active: boolean) => void;

  // For testing only
  reset: () => void;
}

/**
 * Store to manage script items and their state for live guidance.
 * This component receives script suggestions from the LLM and displays them to the user.
 * The LLM is responsible for all logic about what to suggest next.
 */
export const useScriptStore = create<ScriptState>((set, get) => ({
  currentItem: null,
  warmUpItem: null,
  isWaitingForNewWarmUp: false,
  history: [],
  transcriptEvents: [],
  recapItems: [],
  callComplete: false,
  hasActiveCall: false,

  insertScriptItem: (
    text: string,
    tags?: string[],
    metadata?: Record<string, unknown>,
  ) =>
    set({
      currentItem: {
        id: Date.now().toString(),
        text,
        isComplete: false,
        isWarmUp: false,
        tags,
        metadata,
      },
      hasActiveCall: true, // Mark that we have an active call when we get script items
    }),

  warmUpNextScriptItem: (text: string) =>
    set({
      warmUpItem: text,
    }),

  completeScriptItem: () =>
    set((state) => {
      if (!state.currentItem) return state;

      // Add to history
      const completedItem = {
        ...state.currentItem,
        isComplete: true,
      };

      const updatedHistory = [...state.history, completedItem];

      // Mark current item as complete
      const updatedState = {
        ...state,
        currentItem: { ...state.currentItem, isComplete: true },
        history: updatedHistory,
      };

      // Set a timeout to advance to the next item
      setTimeout(() => {
        set((innerState) => {
          if (innerState.warmUpItem) {
            return {
              currentItem: {
                id: Date.now().toString(),
                text: innerState.warmUpItem,
                isComplete: false,
                isWarmUp: true,
              },
              warmUpItem: null,
              isWaitingForNewWarmUp: true,
            };
          }
          return {
            currentItem: {
              id: Date.now().toString(),
              text: DEFAULT_MESSAGE,
              isComplete: false,
              isWarmUp: true,
            },
            isWaitingForNewWarmUp: true,
          };
        });

        // Reset the waiting flag after a delay
        setTimeout(() => {
          set({ isWaitingForNewWarmUp: false });
        }, 2000);
      }, 500);

      return updatedState;
    }),

  addTranscriptEvent: (event: TranscriptEvent) =>
    set((state) => {
      return {
        transcriptEvents: [
          ...state.transcriptEvents,
          {
            ...event,
            id: event.id || Date.now().toString(),
            timestamp: event.timestamp || Date.now(),
          },
        ],
      };
    }),

  setRecapItems: (items: RecapItem[]) =>
    set({
      recapItems: items,
    }),

  completeCall: (outcome: string) =>
    set({
      callComplete: true,
      callOutcome: outcome,
    }),

  // New method to check if there are any script items (current or in history)
  checkForScriptItems: () => {
    const state = get();
    return state.currentItem !== null || state.history.length > 0;
  },

  // Set the active call status
  setHasActiveCall: (active: boolean) =>
    set({
      hasActiveCall: active,
    }),

  // Reset the store state (useful for testing)
  reset: () =>
    set({
      currentItem: null,
      warmUpItem: null,
      isWaitingForNewWarmUp: false,
      history: [],
      transcriptEvents: [],
      recapItems: [],
      callComplete: false,
      callOutcome: undefined,
      hasActiveCall: false,
    }),
}));

// For testing purposes - sample suggestions
export const sampleScriptSuggestions = [
  "Hi [Customer Name], thanks for reaching out about [Product/Service]. Could you tell me more about what led you to book this call?",
  "What's the main problem you're looking to solve?",
  "Understood. Why is solving this a priority now?",
  "If we were to help solve [problem], by what exact date would you want to start implementing a solution?",
  "Are you the main decision-maker for this purchase?",
  "Is there a dedicated budget allocated for this project?",
  "Great. Can you share the approximate amount allocated?",
  "Let me recap what we've discussed:\nProblem: [X]\nPriority: [Y - Why now?]\nStart date: [Z - Exact date or timeframe agreed]\nDecision-maker: [Name(s)]\nBudget: [Figure or plan]\nDo I have all of that right? Anything I've missed or misunderstood?",
  "Perfect. Based on this, I'd like to schedule a deeper conversation with one of our AEs to see how we can help you achieve [desired outcome]. Does [Date/Time] work?",
  "Thank you for your time today. I look forward to our next conversation!",
];

// For testing purposes - sample customer responses
export const sampleCustomerResponses = [
  "Hi there! Yes, I've been looking at solutions like yours and wanted to learn more.",
  "We're struggling with [specific problem] and it's costing us a lot of time and resources.",
  "It's becoming a real bottleneck in our operations. We need to solve it soon.",
  "We're hoping to have something in place by the end of the quarter.",
  "Yes, I'm the one who makes these decisions for my department.",
  "We have about $50,000 set aside for this initiative.",
  "Yes, that sounds right. You've covered everything we discussed.",
  "Tuesday at 2 PM works for me. Should I invite anyone else from my team?",
  "Thanks for your help today. Looking forward to the next call!",
];

// For the conversation simulator - a complete back-and-forth dialogue
export const sampleDialogue = [
  {
    id: "1",
    speaker: "customer",
    text: "Hi there, I got your email about your product and wanted to learn more.",
    timestamp: Date.now(),
  },
  {
    id: "2",
    speaker: "sdr",
    text: "Hi! I'm glad you reached out. Could you tell me a bit about what led you to explore our solution?",
    timestamp: Date.now() + 5000,
  },
  {
    id: "3",
    speaker: "customer",
    text: "We're currently having issues with our sales team's productivity. They're spending too much time on non-sales activities and not enough time closing deals.",
    timestamp: Date.now() + 10000,
  },
  {
    id: "4",
    speaker: "sdr",
    text: "I understand. That's a common challenge we hear. Could you tell me more specifically what kind of non-sales activities are consuming their time?",
    timestamp: Date.now() + 15000,
  },
  {
    id: "5",
    speaker: "customer",
    text: "They're spending hours creating custom presentations, searching for information in our knowledge base, and transcribing call notes. It's really inefficient.",
    timestamp: Date.now() + 20000,
  },
  {
    id: "6",
    speaker: "sdr",
    text: "I see. And how is this impacting your overall sales performance?",
    timestamp: Date.now() + 25000,
  },
  {
    id: "7",
    speaker: "customer",
    text: "Our close rates are down about 15% compared to last year, and our sales cycle has extended by almost two weeks on average.",
    timestamp: Date.now() + 30000,
  },
  {
    id: "8",
    speaker: "sdr",
    text: "That's significant. When would you ideally like to have a solution in place to address these issues?",
    timestamp: Date.now() + 35000,
  },
  {
    id: "9",
    speaker: "customer",
    text: "We're looking to implement something before the end of Q3, so we can see improvements in our Q4 numbers.",
    timestamp: Date.now() + 40000,
  },
  {
    id: "10",
    speaker: "sdr",
    text: "Got it. And do you have a specific budget allocated for this project?",
    timestamp: Date.now() + 45000,
  },
  {
    id: "11",
    speaker: "customer",
    text: "Yes, we've set aside about $50,000 for a solution that can address these efficiency issues.",
    timestamp: Date.now() + 50000,
  },
  {
    id: "12",
    speaker: "sdr",
    text: "Thank you for sharing that. Are you the primary decision-maker for this purchase, or are there others involved in the process?",
    timestamp: Date.now() + 55000,
  },
  {
    id: "13",
    speaker: "customer",
    text: "I'm the Sales Operations Director, so I'll be leading the evaluation, but the final sign-off will come from our VP of Sales and the CFO for purchases of this size.",
    timestamp: Date.now() + 60000,
  },
  {
    id: "14",
    speaker: "sdr",
    text: "I appreciate you clarifying that. Based on what you've shared, our platform could be a great fit. It automates many of those time-consuming tasks you mentioned and has helped similar companies increase their close rates by 20% on average.",
    timestamp: Date.now() + 65000,
  },
  {
    id: "15",
    speaker: "customer",
    text: "That sounds promising. What would be the next steps to learn more and potentially evaluate the platform?",
    timestamp: Date.now() + 70000,
  },
  {
    id: "16",
    speaker: "sdr",
    text: "I'd like to schedule a demo with one of our product specialists who can show you exactly how our platform addresses your specific challenges. Would you be available next Tuesday at 2 PM for a 45-minute session?",
    timestamp: Date.now() + 75000,
  },
  {
    id: "17",
    speaker: "customer",
    text: "Tuesday at 2 PM works for me. Should I invite our VP of Sales to this demo as well?",
    timestamp: Date.now() + 80000,
  },
  {
    id: "18",
    speaker: "sdr",
    text: "That would be great! Having the VP of Sales there would definitely be valuable. I'll send you a calendar invite with all the details, and I'll include some materials you can review before the demo.",
    timestamp: Date.now() + 85000,
  },
  {
    id: "19",
    speaker: "customer",
    text: "Perfect. Thanks for your help today. I'm looking forward to the demo.",
    timestamp: Date.now() + 90000,
  },
  {
    id: "20",
    speaker: "sdr",
    text: "Thank you as well! I'm excited to show you how we can help improve your sales efficiency. Have a great day, and I'll be in touch with that calendar invite shortly.",
    timestamp: Date.now() + 95000,
  },
];
